import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, User, LogOut } from "lucide-react";

const Marquee = () => (
  <div className="border-y-2 border-[#1F3D2A] bg-[#1F3D2A] text-white py-3 overflow-hidden">
    <div className="marquee">
      <div className="marquee-inner mono uppercase text-sm tracking-[0.3em] whitespace-nowrap">
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8">
            JUBASMASH — INDEPENDENT ARTISANS — BOOKS • MUSIC • OBJECTS
            <span className="text-[#C77E3C]">★</span>
          </span>
        ))}
      </div>
    </div>
  </div>
);

const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();

  const dashHref = user
    ? user.role === "admin" ? "/admin"
      : user.role === "seller" ? "/seller" : "/buyer"
    : "/login";

  return (
    <header className="sticky top-0 z-40 bg-[#EDE5D2] border-b-2 border-[#1F3D2A]">
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="heading text-2xl font-black tracking-tighter uppercase">
          JUBA<span className="text-[#C77E3C]">SMASH</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 mono text-xs uppercase tracking-[0.2em]">
          <Link to="/?cat=book" data-testid="nav-books" className="hover:text-[#C77E3C]">Books</Link>
          <Link to="/?cat=music" data-testid="nav-music" className="hover:text-[#C77E3C]">Music</Link>
          <Link to="/?cat=art" data-testid="nav-art" className="hover:text-[#C77E3C]">Objects</Link>
          <Link to="/?cat=apparel" data-testid="nav-apparel" className="hover:text-[#C77E3C]">Apparel</Link>
          <Link to="/?cat=stream" data-testid="nav-streams" className="hover:text-[#C77E3C]">Streams</Link>
          <Link to="/?cat=ticket" data-testid="nav-tickets" className="hover:text-[#C77E3C]">Tickets</Link>
          <Link to="/blog" data-testid="nav-blog" className="hover:text-[#C77E3C]">Blog</Link>
          <Link to="/sell" data-testid="nav-sell" className="hover:text-[#C77E3C]">Sell on Juba</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="cart-link" className="relative brut-border bg-white px-3 py-2 hover:bg-[#C77E3C] hover:text-white transition-colors">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#C77E3C] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center brut-border">{count}</span>
            )}
          </Link>
          {user ? (
            <>
              <Link to={dashHref} data-testid="dashboard-link" className="brut-border bg-white px-3 py-2 hover:bg-[#1F3D2A] hover:text-white transition-colors">
                <User className="w-4 h-4" />
              </Link>
              <Link to="/account" data-testid="account-link" className="hidden md:inline-flex brut-border bg-white px-3 py-2 mono uppercase text-[10px] tracking-[0.25em] hover:bg-[#1F3D2A] hover:text-white transition-colors">Settings</Link>
              <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="brut-border bg-white px-3 py-2 hover:bg-[#1F3D2A] hover:text-white transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="login-link" className="btn-primary text-xs py-2 px-4">Sign in</Link>
          )}
        </div>
      </div>
      <Marquee />
    </header>
  );
};

const Footer = () => (
  <footer className="border-t-2 border-[#1F3D2A] bg-[#1F3D2A] text-white mt-24">
    <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
      <div>
        <div className="heading text-3xl font-black uppercase">JUBA<span className="text-[#C77E3C]">SMASH</span></div>
        <p className="mt-3 text-sm text-white/70 leading-relaxed">An independent storefront for artisans. Books pressed, songs cut, vessels thrown.</p>
      </div>
      <div>
        <h4 className="mono text-xs uppercase tracking-[0.25em] text-[#C77E3C] mb-3">Shop</h4>
        <ul className="space-y-2 text-sm text-white/80">
          <li><Link to="/?cat=book">Books</Link></li>
          <li><Link to="/?cat=music">Music</Link></li>
          <li><Link to="/?cat=art">Objects</Link></li>
          <li><Link to="/?cat=apparel">Apparel</Link></li>
          <li><Link to="/?cat=stream">Streams</Link></li>
          <li><Link to="/?cat=ticket">Tickets</Link></li>
          <li><Link to="/blog">Blog</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="mono text-xs uppercase tracking-[0.25em] text-[#C77E3C] mb-3">Sellers</h4>
        <ul className="space-y-2 text-sm text-white/80">
          <li><Link to="/sell">Apply to sell</Link></li>
          <li><Link to="/seller">Seller dashboard</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="mono text-xs uppercase tracking-[0.25em] text-[#C77E3C] mb-3">Hello</h4>
        <p className="text-sm text-white/70">hello@jubasmash.com</p>
        <p className="mono text-xs text-white/40 mt-6">© 2026 Jubasmash</p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
